import React, { useState, useEffect } from "react";
import { useTheme } from '@repo/shared';
import { Container, Card, Form, Button, Spinner } from 'react-bootstrap';
import ConfigDataService from "../../services/config/ConfigService";
import LoadingSpinner from '../common/LoadingSpinner';

export default function EmailConfig() {
  const [emailConfig, setEmailConfig] = useState({
    username: "",
    host: "",
    password: "",
  });
  const [originalConfig, setOriginalConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("info");
  const [hasChanges, setHasChanges] = useState(false);

  // Test email fields
  const [testEmail, setTestEmail] = useState({
    toAddresses: "",
    subject: "Test Email",
    emailBody: "",
  });
  const [testMessage, setTestMessage] = useState(null);
  const [testMessageType, setTestMessageType] = useState("info");
  const [sendingTest, setSendingTest] = useState(false);

  const { appTheme } = useTheme();

  useEffect(() => {
    document.title = `${appTheme?.appName} - Config`;
    retrieveEmailConfig();
  }, []);

  const retrieveEmailConfig = () => {
    setLoading(true);
    ConfigDataService.getEmailConfig()
      .then((response) => {
        const config = {
          username: response?.username || "",
          host: response?.host || "",
          password: response?.password || "",
        };
        setEmailConfig(config);
        setOriginalConfig(config);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setMessage("Error loading email configuration");
        setMessageType("danger");
        setLoading(false);
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedConfig = { ...emailConfig, [name]: value };
    setEmailConfig(updatedConfig);

    // Check if there are any changes
    const changed = JSON.stringify(updatedConfig) !== JSON.stringify(originalConfig);
    setHasChanges(changed);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await ConfigDataService.saveEmailConfig(emailConfig);
      setOriginalConfig(emailConfig);
      setHasChanges(false);
      setMessage("Email configuration saved successfully");
      setMessageType("success");
    } catch (e) {
      console.log(e);
      setMessage("Error saving email configuration");
      setMessageType("danger");
    }
    setSaving(false);
  };

  const handleReset = () => {
    setEmailConfig(originalConfig);
    setHasChanges(false);
    setMessage(null);
  };

  const handleTestEmailChange = (e) => {
    const { name, value } = e.target;
    setTestEmail({ ...testEmail, [name]: value });
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.toAddresses.trim()) {
      setTestMessage("Please enter a recipient email address");
      setTestMessageType("warning");
      return;
    }

    setSendingTest(true);
    setTestMessage(null);
    try {
      const toAddressesList = testEmail.toAddresses
        .split(',')
        .map(addr => addr.trim())
        .filter(addr => addr.length > 0);

      const response = await ConfigDataService.sendEmail(
        toAddressesList,
        testEmail.subject || "Test Email",
        testEmail.emailBody
      );

      setTestMessage("Test email sent successfully!");
      setTestMessageType("success");
      setTestEmail({ toAddresses: "", subject: "Test Email", emailBody: "" });
    } catch (e) {
      console.log(e);
      setTestMessage("Error sending test email: " + (e.response?.data?.message || e.message));
      setTestMessageType("danger");
    }
    setSendingTest(false);
  };

  const handleResetTestEmail = () => {
    setTestEmail({ toAddresses: "", subject: "Test Email", emailBody: "" });
    setTestMessage(null);
  };

  const renderBody = () => {
    return (
      <>
        <Card className="mt-4">
          <Card.Header as="h5">Configuration</Card.Header>
          <Card.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Email Username</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  value={emailConfig.username}
                  onChange={handleInputChange}
                  placeholder="Enter email username"
                />
                <Form.Text className="text-muted">
                  The email address or username used to send emails
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email Host</Form.Label>
                <Form.Control
                  type="text"
                  name="host"
                  value={emailConfig.host}
                  onChange={handleInputChange}
                  placeholder="e.g., smtp.gmail.com"
                />
                <Form.Text className="text-muted">
                  The SMTP server host for sending emails
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={emailConfig.password}
                  onChange={handleInputChange}
                  placeholder="Enter email password"
                />
                <Form.Text className="text-muted">
                  The password or app-specific password for the email account
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    "Save Configuration"
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleReset}
                  disabled={!hasChanges}
                >
                  Reset
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
        <Card className="mt-4">
          <Card.Header as="h5">Test</Card.Header>
          <Card.Body>

            {testMessage && (
              <Alert variant={testMessageType} dismissible onClose={() => setTestMessage(null)}>
                {testMessage}
              </Alert>
            )}

            <Form>
              <Form.Group className="mb-3">
                <Form.Label>To (Email Address)</Form.Label>
                <Form.Control
                  type="text"
                  name="toAddresses"
                  value={testEmail.toAddresses}
                  onChange={handleTestEmailChange}
                  placeholder="Enter recipient email(s), comma-separated for multiple"
                />
                <Form.Text className="text-muted">
                  You can enter multiple addresses separated by commas
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  type="text"
                  name="subject"
                  value={testEmail.subject}
                  onChange={handleTestEmailChange}
                  placeholder="Enter email subject"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  name="emailBody"
                  rows={5}
                  value={testEmail.emailBody}
                  onChange={handleTestEmailChange}
                  placeholder="Enter the email message body"
                />
              </Form.Group>

              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleSendTestEmail}
                  disabled={sendingTest}
                >
                  {sendingTest ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Sending...
                    </>
                  ) : (
                    "Send Test Email"
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleResetTestEmail}
                >
                  Clear
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </>
    )
  }
  return (
    <Container className="my-4">
      {/* MODERNIZED: Page is wrapped in a main Card for a modern look */}
      <Card className="h-100 shadow-sm">
        <Card.Header className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center gap-2">
            <h2 className="mb-0">Email Setup</h2>
          </div>
        </Card.Header>
        <Card.Body className="p-4">
          {loading ? <div className='loadingSpinner'><LoadingSpinner /></div> : renderBody()}
        </Card.Body>
      </Card>
    </Container>
  );

}
